"use client";

import { useState, useEffect } from "react";
import { useMotion } from "@/contexts/MotionContext";
import { motion, AnimatePresence } from "framer-motion";
import Quote from "@/components/Quote";
import Image from "next/image";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

interface Testimonial {
  quote: string;
  author: string;
  role: string;
  image: string;
}

const TESTIMONIALS: Testimonial[] = [
  {
    quote:
      "Township has been fantastic to work with; they are responsive and capable problem solvers, and willing to adapt as the project has adapted. I’d recommend them to anyone.",
    author: "Dr. Emily Oster",
    role: "Author and Professor of Economics at Brown University",
    image: "/assets/logos/oster.webp",
  },
  {
    quote:
      "Working with Township has been a gamechanger for us. They have a knack for understanding and amplifying our mission, helping us tell our story more effectively through both our website and mobile app. For us, they weren’t just an agency, they were a strategic partner that helped us get our brand and product off the ground.",
    author: "Nishi Patel Murphy",
    role: "Head of Product, Fabrik",
    image: "/assets/logos/nishi.jpeg",
  },
  {
    quote:
      "Township’s rare and additive approach to start with the strategy, spec, and design work up front before writing a line of code not only saves budget, resources, and time, but also helps bring different teams and constituents along with you as you create something new.",
    author: "Shea Tate-Di Donna",
    role: "Author, The Venture Fund Blueprint; Founding Team Emeritus, True Ventures",
    image: "/assets/logos/shea.jpeg",
  },
];

export function TestimonialsSection() {
  const { isMotionEnabled } = useMotion();
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!isMotionEnabled) return;

    const timer = setInterval(() => {
      setCurrentIndex((current) =>
        current === TESTIMONIALS.length - 1 ? 0 : current + 1,
      );
    }, 8000);

    return () => clearInterval(timer);
  }, [isMotionEnabled]);

  const testimonial = TESTIMONIALS[currentIndex];

  return (
    <section className="py-8">
      <div className="container max-w-7xl">
        <div className="relative min-h-[400px] md:h-[500px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="absolute inset-0 flex items-center"
            >
              <Quote
                text={testimonial.quote}
                type="testimonial"
                author={testimonial.author}
                role={testimonial.role}
                className="min-h-[400px] md:min-h-[500px] flex flex-col justify-center w-full"
              >
                <div className="flex items-center">
                  <Image
                    src={testimonial.image}
                    alt={testimonial.author}
                    width={48}
                    height={48}
                    className="w-10 h-10 md:w-12 md:h-12 rounded-full"
                  />
                </div>
              </Quote>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="flex justify-center items-center gap-4 mt-8">
          <button
            onClick={() =>
              setCurrentIndex((prev) =>
                prev === 0 ? TESTIMONIALS.length - 1 : prev - 1,
              )
            }
            className="flex items-center justify-center w-8 h-8 rounded-full bg-white/50 hover:bg-white/80 transition-colors"
            aria-label="Previous testimonial"
          >
            <ChevronLeftIcon className="w-4 h-4" />
          </button>

          <div className="flex justify-center gap-3">
            {TESTIMONIALS.map((testimonial, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentIndex
                    ? "bg-gray-800 dark:bg-white"
                    : "bg-gray-300 dark:bg-gray-700"
                }`}
                aria-label={`Go to testimonial ${index + 1} - ${testimonial.author}`}
                aria-current={index === currentIndex ? "true" : "false"}
              />
            ))}
          </div>

          <button
            onClick={() =>
              setCurrentIndex((prev) =>
                prev === TESTIMONIALS.length - 1 ? 0 : prev + 1,
              )
            }
            className="flex items-center justify-center w-8 h-8 rounded-full bg-white/50 hover:bg-white/80 transition-colors"
            aria-label="Next testimonial"
          >
            <ChevronRightIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}
